"use client";

import { useEffect, useRef, useState } from "react";

/** Open/close transition, mirrored in every inline `transition` that reads it. */
export const ANIM_MS = 220;

/**
 * How far down the homepage the bar waits before appearing. Applies ONLY where
 * a `[data-hero-search]` exists, and is paired with that element leaving the
 * viewport, so the hero input never competes with the docked bar for the same
 * intent. Pages without a hero search show the bar immediately.
 */
const REVEAL_AFTER_PX = 240;

/**
 * The reveal gate, shared by both docked variants (`mini-chat-spec.md`,
 * `mini-search-fallback-spec.md` §7). Extracted from `MiniChatBar` when the
 * search fallback arrived — it is the same gate, and the bugs below were paid
 * for once already.
 *
 * ⭐ It reads the PRESENCE of `[data-hero-search]`, not a route: both hero
 * variants (assistant and search) carry that attribute, and a path check would
 * have to be kept in step with wherever the hero is used.
 *
 * `enabled` exists because a caller may decide it renders NOTHING on this route
 * (the search variant is suppressed outright on `/search`) — and a hook cannot
 * move below that early return. Without it, the most heavily scrolled page on
 * the site pays for an IntersectionObserver plus scroll and resize listeners
 * driving a component that returns `null`.
 */
export function useHeroRevealGate(pathname: string | null, enabled = true): boolean {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    // ⛔ PRESENCE IS NOT ENOUGH — it must be RENDERED. After a client-side
    // navigation the App Router leaves the previous page's DOM in the tree, so
    // on /insights this selector still finds the HOMEPAGE's hero, at height 0.
    // The effect then took the hero branch and sat waiting for a scroll the new
    // page has no reason to require, and the bar never appeared (measured
    // 2026-09-07: `heroStillInDom: true, heroRect: 0` on /insights, reached by
    // clicking the nav link; a full page load of the same URL showed the bar).
    // A zero-size box is not on screen by any definition, which is the only
    // thing this gate is actually asking about.
    const candidate = document.querySelector<HTMLElement>("[data-hero-search]");
    const hero = candidate && candidate.getBoundingClientRect().height > 0 ? candidate : null;
    if (!hero) {
      const once = requestAnimationFrame(() => setRevealed(true));
      return () => cancelAnimationFrame(once);
    }
    let heroGone = false;
    let scrolled = false;
    const apply = () => setRevealed(scrolled && heroGone);
    const onScroll = () => { scrolled = window.scrollY > REVEAL_AFTER_PX; apply(); };
    const io = new IntersectionObserver(([e]) => { heroGone = !e.isIntersecting; apply(); });
    io.observe(hero);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    const raf = requestAnimationFrame(onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      io.disconnect();
    };
    // ⛔ `pathname`, NOT `[]` — the gate is PER ROUTE and the components that
    // call this never unmount (they are mounted in the marketing LAYOUT, which
    // survives every client-side navigation). With `[]` the effect ran once,
    // against whatever page happened to be first, and its verdict then followed
    // the reader around the site.
    //
    // Measured 2026-09-07, with a full page load as the control arm: landing on
    // the homepage and clicking through to /insights left the bar hidden there
    // forever, because the "no hero → reveal immediately" branch never re-ran —
    // while loading /insights directly showed it. Same viewport, same page, only
    // the navigation differed.
    //
    // Re-running is cheap (one querySelector, one IO) and self-correcting: on a
    // page WITH a hero the first `onScroll` immediately recomputes `revealed`
    // from that page's own scroll position, so a stale `true` cannot leak onto
    // the homepage either.
  }, [pathname, enabled]);
  // Disabled means "this route renders nothing", not "not yet revealed" — so
  // never hand back a stale `true` from the route the reader just left.
  return enabled ? revealed : false;
}

interface MiniBarShellProps {
  /** Is the pill on screen? Below this it slides out of the bottom edge. */
  onScreen: boolean;
  reducedMotion: boolean;
  /** Hide from `lg` up — `miniChatDesktopSuppressed` for this route. */
  desktopSuppressed?: boolean;
  /**
   * The assistant's expanded panel wears the pop radius. A resting pill — which
   * is every state the search variant has — stays fully round.
   */
  expanded?: boolean;
  /**
   * Clip the pill's contents. TRUE for the assistant, whose panel grows a
   * square-cornered body inside a round box. FALSE for the search fallback,
   * which hangs its suggestion dropdown ABOVE the pill — clipping there hides
   * the dropdown entirely. Safe to turn off because every child is inset by the
   * row's padding, so nothing reaches a corner to paint over it.
   */
  clip?: boolean;
  panelRef?: React.Ref<HTMLDivElement>;
  role?: "dialog";
  ariaLabel?: string;
  children: React.ReactNode;
}

/**
 * Corner-radius timing -- and the radius is deliberately NOT interpolated.
 *
 * ⛔ `rounded-full` is a ~1.7e7px length that CSS clamps to half the box height.
 * Animating it toward `rounded-pop` interpolates the RAW huge number, so it
 * stays past the clamp -- i.e. visually fully round -- for essentially the whole
 * duration and then snaps at the end. Measured at 220ms, the panel was still
 * fully round at 250px tall: a giant stadium mid-expansion, which is exactly the
 * state the owner rejected. Shortening the duration only shortens that window;
 * it never removes it. So the radius steps (0ms duration) instead:
 *   opening -- immediately, before the box has grown enough to look wrong;
 *   closing -- held square until the box has nearly finished shrinking.
 */
const RADIUS_SETTLE_MS = ANIM_MS - 40;

/**
 * The docked pill's position, glass and slide — everything both variants share.
 * The contents differ; this box does not.
 *
 * ⚠️ No portal, unlike `MobileMenu`. That one had to escape `NavScrollWrapper`,
 * which is `fixed` + `transform` and therefore a containing block AND a
 * stacking context. This mounts as a plain child of the marketing layout, which
 * is neither, so `position: fixed` resolves against the viewport as intended.
 * If it is ever moved inside a transformed ancestor, it will need the portal.
 */
export default function MiniBarShell({
  onScreen,
  reducedMotion,
  desktopSuppressed = false,
  expanded = false,
  clip = true,
  panelRef,
  role,
  ariaLabel,
  children,
}: MiniBarShellProps) {
  // ⭐ CLEARANCE FOR THE FOOTER (owner, 2026-10-09: "mini chat is hiding
  // footer elements"). The bar floats over the page's last strip, so the
  // footer's copyright row and social links could never scroll clear of it.
  // While the bar is on screen it publishes its RESTING height as
  // `--mini-bar-clearance`, and the footer adds that much space below its last
  // row. The open panel is not counted (it is meant to cover the page while in
  // use; counting it would make the footer jump on every open), and a bar
  // hidden by `lg:hidden` measures 0, so desktop pages that suppress it keep
  // their footer as it was.
  const wrapRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef(expanded);
  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);
  useEffect(() => {
    const el = wrapRef.current;
    const root = document.documentElement;
    if (!el || !onScreen) {
      root.style.removeProperty("--mini-bar-clearance");
      return;
    }
    const apply = () => {
      if (expandedRef.current) return;
      root.style.setProperty("--mini-bar-clearance", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--mini-bar-clearance");
    };
  }, [onScreen]);

  return (
    <div
      ref={wrapRef}
      // The bar is shown on DESKTOP (owner, 2026-09-07): it was mobile-only
      // when the desktop header still carried the assistant link, the hero
      // carries it now, and the docked pill is what the reader has once they
      // scroll past the hero. The `[data-hero-search]` reveal gate already
      // stops the two appearing together at ANY width.
      //
      // ⚠️ EXCEPT on /search and a professional profile, where `lg:hidden`
      // comes back — see `miniChatDesktopSuppressed`. Those two give the
      // reader a persistent desktop affordance of their own (the filter
      // sidebar; the contact card), and the pill competes with it. Below `lg`
      // neither is persistent, so the bar still shows there.
      //
      // ⭐ `pointer-events-none`, with the panel below re-enabling itself.
      // This wrapper spans the full width so the pill can centre in it, but
      // the pill is only 512px from `sm` up -- so at tablet width it left two
      // INVISIBLE 120x64px strips either side that swallowed every click on
      // whatever sat beneath them at the bottom of the page (footer links,
      // the feedback tab). Measured at 768px, 2026-09-06. It never showed at
      // 375px, where the pill is within 8px of the viewport edge.
      //
      // z-sticky, not an overlay layer: it is chrome, and every modal's scrim
      // (z-overlay) must dim it like the rest of the page (owner, 2026-10-14).
      className={`pointer-events-none fixed inset-x-0 bottom-0 z-sticky px-8${desktopSuppressed ? " lg:hidden" : ""}`}
      style={{
        // The home indicator sits under a bottom-docked bar on modern phones.
        paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))",
        // Slides out of the bottom edge rather than popping, and goes
        // `visibility: hidden` so a bar the reader cannot see is also out of
        // the tab order and the accessibility tree. The visibility delay is
        // asymmetric on purpose: instant on the way in, held until the slide
        // finishes on the way out.
        transform: onScreen ? "none" : "translateY(calc(100% + 8px))",
        opacity: onScreen ? 1 : 0,
        visibility: onScreen ? "visible" : "hidden",
        transition: reducedMotion
          ? "none"
          : `transform ${ANIM_MS}ms cubic-bezier(.2,0,0,1), opacity 160ms ease, visibility 0s linear ${onScreen ? 0 : ANIM_MS}ms`,
      }}
    >
      {/* ⭐ SAME-SIZE WRAPPER FOR THE BORDER SWEEP (2026-09-17). The ring
          (`.shine-ring`) sits ON the pill's border, 1px of it outside, and the
          assistant pill is `overflow-hidden`, which would cut that pixel off.
          So the ring is the pill's SIBLING, after it in the DOM so it paints
          above the pill's edge, inside this wrapper that is exactly the pill's
          size. The width classes and the reveal timing variables moved here
          from the pill for the same reason. */}
      <div
        className="relative mx-auto w-full sm:w-512"
        style={{
          // The ring is hidden while the panel is open (owner, 2026-09-13) and
          // comes back only after the panel has finished shrinking: instantly
          // gone on open, a short fade after ANIM_MS on close. That delay also
          // outlasts RADIUS_SETTLE_MS, so the band is never seen against the
          // pop corners the panel holds while closing.
          "--shine-fade": reducedMotion || expanded ? "0ms" : "200ms",
          "--shine-reveal-delay": reducedMotion || expanded ? "0ms" : `${ANIM_MS}ms`,
        } as React.CSSProperties}
      >
        <div
          ref={panelRef}
          role={role}
          aria-label={ariaLabel}
          // ⭐ A stadium pill while minimised (owner, 2026-09-06); the pop
          // radius returns as soon as it is a panel with square content in it.
          //
          // ⭐ Translucent + blurred (owner, 2026-09-08). `bg-white/90` +
          // `backdrop-blur-md` is the house treatment for chrome that floats
          // over page content — the same pair the Nav, the cookie bar and the
          // locale strip already use — so the bar reads as glass sitting on the
          // page rather than a card pasted onto it.
          //
          // ⚠️ /90 is as far as this goes, and the limit is CONTRAST, not taste.
          // The bar drifts over dark sections — measured behind it on the navy
          // "Popular services" band: rgb(4,0,68). Every point of alpha pulls the
          // composited ground toward that navy while the typewriter placeholder
          // stays `text-gray-500`, the token chosen to hold WCAG AAA on white.
          //
          // MEASURED at /90: the ground composites to rgb(230,230,236), and
          // gray-500 against it is 5.59:1 — down from 6.95:1 on pure white. So
          // this trade has already spent the AAA margin the token exists for and
          // now sits at AA. Typed text is `text-navy` and unaffected. Going
          // further (or picking a darker page section) needs the placeholder
          // colour to move with it, not just a lower alpha.
          //
          // ⭐ THE GLASS IS FOR THE RESTING PILL ONLY (owner, 2026-09-12). Open,
          // the panel is SOLID WHITE with no blur: it stops being chrome floating
          // over the page and becomes the thing the reader is using. (It sat over
          // a navy scrim until 2026-09-13; the owner removed the scrim, and the
          // panel now just expands over a lit page, which solid white suits.) Two things follow, both
          // in the right direction: the placeholder is back on pure white, so
          // `text-gray-500` regains its AAA 6.95:1 instead of the 5.59:1 measured
          // over /90, and the panel stops paying for a backdrop filter over its
          // largest area while a light animates on its edge.
          //
          // ⛔ `backdrop-filter` makes an element a containing block for FIXED
          // descendants. Safe here only because there are none inside the panel
          // — the positioned wrapper is the PARENT.
          // Anything `fixed` added in here later must portal out. Note the open
          // panel drops the filter, so that hazard exists in the pill state only.
          //
          // `relative`: the ring used to be a child here and needed this box to
          // contain it (before 2026-09-13 only the pill's `backdrop-filter` did,
          // by accident, and the light traced the full-width fixed wrapper when
          // the open panel dropped the filter). Since 2026-09-17 the ring is a
          // sibling in the same-size wrapper above, which is `relative` itself.
          className={`pointer-events-auto relative w-full border border-card-line shadow-e5 ${expanded ? "bg-white" : "bg-white/90 backdrop-blur-md"} ${clip ? "overflow-hidden" : ""} ${expanded ? "rounded-pop" : "rounded-full"}`}
          style={{
            transition: reducedMotion
              ? "none"
              : `border-radius 0ms linear ${expanded ? 0 : RADIUS_SETTLE_MS}ms`,
          }}
        >
          {children}
        </div>
        {/* Decorative colour band sweeping across the border, the same one the
            hero assistant wears: the docked bar is that same assistant, so it
            carries the same mark. Empty and aria-hidden, gone under reduced
            motion, and hidden and paused while the panel is open
            (`.shine-ring-hidden`), so it only ever draws against the pill. */}
        <span aria-hidden="true" className={`shine-ring shine-ring-pill ${expanded ? "shine-ring-hidden" : ""}`} />
      </div>
    </div>
  );
}
